import React, { ReactNode, useEffect, useState } from 'react';
import { makeStyles, Button } from "@fluentui/react-components";
import { QuestionCircle24Regular, Globe24Regular, ArrowLeft24Regular } from '@fluentui/react-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigateSafely } from '../../utils/navigation';
import { useTranslation } from 'react-i18next';
import { getEnvironment360URL } from '../../services/configs/appConfig';
import { getConfigs } from '../../services/api/dataService';
import { formatVersionForHelpUrl } from '../../utils/Helper';
import i18n from '../../i18n';

const useStyles = makeStyles({
    headerItems: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
    },
    item:
    {
        padding: '2px'
    },
    itemHide: {
        padding: '2px',
        visibility: 'hidden'
    },
    backButton: {
        padding: '2px',
        minWidth: 'auto',
        border: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: '#f3f2f1',
        },
    },
    root:
    {
        minWidth: "280px",
        maxWidth: "320px",
    },
    childrenroot:
    {
        display: 'flex',
        // minHeight: "579px",
        alignItems: "center",
        flexDirection: "column"
    }
});


const openLink = (link: string, event?: React.MouseEvent<HTMLElement>): void => {
    if (event) {
        event.preventDefault();
    }
    // Force to open in default browser if outlook app client is beeing used
    if ((Office.context.diagnostics.platform === Office.PlatformType.PC || Office.context.diagnostics.platform === Office.PlatformType.Mac)
        && Office.context.diagnostics.host === Office.HostType.Outlook && Office.context.requirements.isSetSupported('Mailbox', '1.2')) {
        Office.context.ui.openBrowserWindow(link);
    } else {
        window.open(link);
    }
};

const open360 = (event?: React.MouseEvent<HTMLElement>): void => {
    openLink(getEnvironment360URL(), event);
};



const Header: React.FC<{ children: ReactNode }> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [p360version, setP360version] = useState('');

    useEffect(() => {
        const cacheKey = `helpVersion:${getEnvironment360URL()}`;
        const cachedVersion = sessionStorage.getItem(cacheKey);

        if (cachedVersion) {
            setP360version(cachedVersion);
            return;
        }

        let isMounted = true;

        getConfigs()
            .then(config => {
                if (isMounted && config?.VersionOf360ForHelp) {
                    const version = formatVersionForHelpUrl(config.VersionOf360ForHelp);
                    sessionStorage.setItem(cacheKey, version);
                    setP360version(version);
                }
            })
            .catch(() => undefined);

        return () => {
            isMounted = false;
        };
    }, []);

    // Check if we're on the QuickSave route
    const isBackNavigationRoute = ['/quicksave', '/archiveMultipleMail'].includes(location.pathname);

    const handleBack = () => {
        navigateSafely(navigate, '/archivehome');
    };

    const openHelp = (event?: React.MouseEvent<HTMLElement>) => {
        openLink(i18n.t('HelpPageUrl', { p360version }), event);
    };

    const classes = useStyles();
    return (
        <div>

            <header className={classes.root}>
                <div className={classes.headerItems}>
                    <div>
                        {isBackNavigationRoute ? (
                            <Button
                                appearance="subtle"
                                icon={<ArrowLeft24Regular />}
                                onClick={handleBack}
                                className={classes.backButton}
                                aria-label={t("Back")}
                            >
                                {t("Back")}
                            </Button>
                        ) : (
                            <div className={classes.itemHide}>
                                {/* Placeholder to maintain layout */}
                            </div>
                        )}
                    </div>
                    <div>
                        <Globe24Regular aria-label={t("Open 360")} className={classes.item} onClick={() => open360()} />
                        <QuestionCircle24Regular aria-label={t("Open help")} className={classes.item} onClick={() => openHelp()} />
                    </div>
                </div>
            </header>
            <div className={classes.childrenroot}>
                {children}
            </div>
        </div>
    );
};

export default Header;

